import React, { useState, useEffect, useCallback } from 'react';
import {
    Plus,
    Pencil,
    Trash2,
    Eye,
    EyeOff,
    Trophy,
    Image as ImageIcon,
    X,
    Loader2,
    Save,
    Type,
    Layers,
    Activity,
    ChevronRight
} from 'lucide-react';
import Toast from '../Common/Toast';
import ConfirmDialog from '../Common/ConfirmDialog';
import './SportsManager.css';
import API_BASE_URL from '../../config/api';

const SportsManager = () => {
    const [sports, setSports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingSport, setEditingSport] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [confirmState, setConfirmState] = useState({ isOpen: false, id: null });

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        order: 0,
        isActive: true,
        image: null
    });
    const [previewUrl, setPreviewUrl] = useState(null);

    const addToast = useCallback((message, type = 'success') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    useEffect(() => {
        fetchSports();
    }, []);

    const fetchSports = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/sports/all`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setSports(data.sort((a, b) => a.order - b.order));
            }
        } catch (error) {
            addToast('Erro ao carregar as modalidades', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        const token = localStorage.getItem('token');
        const formDataToSend = new FormData();

        formDataToSend.append('name', formData.name);
        formDataToSend.append('description', formData.description);
        formDataToSend.append('order', formData.order);
        formDataToSend.append('isActive', formData.isActive);

        if (formData.image) {
            formDataToSend.append('image', formData.image);
        }

        try {
            const url = editingSport
                ? `${API_BASE_URL}/api/sports/${editingSport.id}`
                : `${API_BASE_URL}/api/sports`;

            const method = editingSport ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: { 'Authorization': `Bearer ${token}` },
                body: formDataToSend
            });

            if (response.ok) {
                setShowModal(false);
                resetForm();
                fetchSports();
                addToast(editingSport ? 'Modalidade atualizada!' : 'Modalidade criada com sucesso!');
            } else {
                addToast('Erro ao guardar modalidade', 'error');
            }
        } catch (error) {
            addToast('Erro técnico ao processar formulário', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        const id = confirmState.id;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/sports/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                fetchSports();
                addToast('Modalidade removida permanentemente', 'warning');
            } else {
                addToast('Erro ao eliminar modalidade', 'error');
            }
        } catch (error) {
            addToast('Erro ao processar eliminação', 'error');
        } finally {
            setConfirmState({ isOpen: false, id: null });
        }
    };

    const handleEdit = (sport) => {
        setEditingSport(sport);
        setFormData({
            name: sport.name,
            description: sport.description,
            order: sport.order,
            isActive: sport.isActive,
            image: null
        });

        if (sport.imageUrl) {
            setPreviewUrl(getImageUrl(sport.imageUrl));
        }

        setShowModal(true);
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            order: 0,
            isActive: true,
            image: null
        });
        setEditingSport(null);
        setPreviewUrl(null);
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setFormData({ ...formData, image: file });
            const reader = new FileReader();
            reader.onloadend = () => setPreviewUrl(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const getImageUrl = (imageUrl) => {
        if (!imageUrl) return null;
        if (imageUrl.startsWith('http')) return imageUrl;
        return `${API_BASE_URL}${imageUrl.startsWith('/') ? imageUrl : '/' + imageUrl}`;
    };

    if (loading) {
        return (
            <div className="loading-container">
                <Loader2 className="spinner" size={48} />
                <p>A sincronizar lista de modalidades...</p>
            </div>
        );
    }

    return (
        <div className="sports-manager">
            {/* Toasts Container */}
            <div className="toast-container">
                {toasts.map(t => (
                    <Toast key={t.id} {...t} onClose={() => removeToast(t.id)} />
                ))}
            </div>

            {/* Confirmation Dialog */}
            <ConfirmDialog
                isOpen={confirmState.isOpen}
                title="Eliminar Modalidade"
                message="Tens a certeza que desejas remover esta modalidade? Todos os dados associados serão perdidos."
                onConfirm={handleDelete}
                onCancel={() => setConfirmState({ isOpen: false, id: null })}
            />

            <header className="manager-header">
                <div className="header-content">
                    <h1>Sports Manager</h1>
                    <p className="header-subtitle">Configuração de modalidades oficiais e secundárias</p>
                </div>
                <button className="btn-add" onClick={() => { resetForm(); setShowModal(true); }}>
                    <Plus size={20} /> Adicionar Modalidade
                </button>
            </header>

            {sports.length === 0 ? (
                <div className="empty-state glass-card">
                    <Trophy className="empty-icon" size={64} />
                    <h3>Nenhuma modalidade ativa</h3>
                    <p>Comece por definir os desportos praticados no clube</p>
                    <button className="btn-add" onClick={() => setShowModal(true)}>
                        <Plus size={20} /> Definir Modalidade
                    </button>
                </div>
            ) : (
                <div className="sports-list">
                    {sports.map((sport) => (
                        <div key={sport.id} className="sport-row-card">
                            <div className="sport-image-container">
                                {sport.imageUrl ? (
                                    <img src={getImageUrl(sport.imageUrl)} alt={sport.name} className="sport-row-image" />
                                ) : (
                                    <div className="sport-no-image-row"><Trophy size={32} /></div>
                                )}
                                <div className={`sport-row-status ${sport.isActive ? 'active' : 'inactive'}`}>
                                    {sport.isActive ? <Eye size={12} /> : <EyeOff size={12} />}
                                    <span>{sport.isActive ? 'Ativa' : 'Inativa'}</span>
                                </div>
                            </div>

                            <div className="sport-row-content">
                                <div className="sport-title-area">
                                    <span className="sport-order-tag">#{sport.order}</span>
                                    <h3>{sport.name}</h3>
                                </div>
                                <p className="sport-row-description">{sport.description}</p>
                            </div>

                            <div className="sport-row-actions">
                                <button className="row-action-btn edit" onClick={() => handleEdit(sport)}>
                                    <Pencil size={18} />
                                    <span>Editar</span>
                                </button>
                                <button className="row-action-btn delete" onClick={() => setConfirmState({ isOpen: true, id: sport.id })}>
                                    <Trash2 size={18} />
                                </button>
                                <div className="row-chevron">
                                    <ChevronRight size={24} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) { setShowModal(false); resetForm(); } }}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <header className="modal-header">
                            <div>
                                <h2>{editingSport ? 'Editar Modalidade' : 'Configurar Desporto'}</h2>
                                <p className="header-subtitle">Gerir identidade visual e descrições</p>
                            </div>
                            <button className="modal-close" onClick={() => { setShowModal(false); resetForm(); }}><X size={24} /></button>
                        </header>

                        <form onSubmit={handleSubmit} className="modal-form">
                            <div className="form-section-group">
                                <div className="section-label"><Type size={16} /><span>Informação Base</span></div>
                                <div className="form-section">
                                    <label className="form-label">Nome da Modalidade</label>
                                    <input
                                        type="text" className="form-input" value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="Ex: Voleibol, Futsal..." required
                                    />
                                </div>
                                <div className="form-section">
                                    <label className="form-label">Breve Descrição</label>
                                    <textarea
                                        className="form-textarea" value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Descrição para a homepage..." rows="3" required
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-section-group">
                                    <div className="section-label"><Layers size={16} /><span>Visibilidade</span></div>
                                    <div className="form-section">
                                        <label className="form-label">Sequência</label>
                                        <input
                                            type="number" className="form-input" value={formData.order}
                                            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                                            min="0"
                                        />
                                    </div>
                                    <div className="form-section">
                                        <label className="toggle-switch premium-toggle">
                                            <input
                                                type="checkbox" checked={formData.isActive}
                                                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                            />
                                            <span className="toggle-slider"></span>
                                            <span className="toggle-label">{formData.isActive ? 'Ativa' : 'Inativa'}</span>
                                        </label>
                                    </div>
                                </div>

                                <div className="form-section-group">
                                    <div className="section-label"><ImageIcon size={16} /><span>Fotografia</span></div>
                                    <div className="image-upload-area">
                                        {previewUrl ? (
                                            <div className="image-preview premium-preview small">
                                                <img src={previewUrl} alt="Preview" />
                                                <div className="preview-overlay">
                                                    <button type="button" className="btn-change-image" onClick={() => document.getElementById('imageInput').click()}><Pencil size={14} /></button>
                                                </div>
                                            </div>
                                        ) : (
                                            <label htmlFor="imageInput" className="upload-label premium-label small">
                                                <Activity size={24} />
                                                <span>Upload Foto</span>
                                            </label>
                                        )}
                                        <input id="imageInput" type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                                    </div>
                                </div>
                            </div>

                            <footer className="modal-actions premium-actions">
                                <button type="button" className="btn-cancel" onClick={() => { setShowModal(false); resetForm(); }}>Cancelar</button>
                                <button type="submit" className="btn-submit premium-submit" disabled={submitting}>
                                    {submitting ? <Loader2 className="spinner" size={18} /> : (editingSport ? 'Atualizar Dados' : 'Criar Modalidade')}
                                </button>
                            </footer>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SportsManager;